// @ts-nocheck
import type { ValidationIssue } from '../../diagnostics';
import type { AssetRef } from '../../types/canonical';
import type { AssetResolutionContext, AssetResolutionResult } from './types';

const DEFAULT_MAX_INLINE_BYTES = 65536;

function makeErrorIssue(
  code: ValidationIssue['code'],
  message: string
): ValidationIssue {
  return { severity: 'error', code, message };
}

function blockedResult(issue: ValidationIssue): AssetResolutionResult {
  return { status: 'blocked', data: null, mediaType: null, issues: [issue] };
}

function missingResult(issue: ValidationIssue): AssetResolutionResult {
  return { status: 'missing', data: null, mediaType: null, issues: [issue] };
}

function resolvedResult(
  data: string | null,
  mediaType: string | null
): AssetResolutionResult {
  return { status: 'resolved', data, mediaType, issues: [] };
}

function isRemoteUrl(value: string): boolean {
  return value.startsWith('http://') || value.startsWith('https://');
}

function checkRemoteUrlAllowlist(
  url: string,
  allowlist: string[]
): boolean {
  if (allowlist.length === 0) return false;
  return allowlist.some((prefix) => url.startsWith(prefix));
}

/**
 * Resolves a single AssetRef against the provided context and policy.
 * Returns an AssetResolutionResult. Never throws.
 */
export function resolveAsset(
  ref: AssetRef,
  context: AssetResolutionContext
): AssetResolutionResult {
  switch (ref.kind) {
    case 'inline': {
      if (context.allowInlineAssets === false) {
        return blockedResult(makeErrorIssue(
          'policy.capability_not_allowed',
          'Inline assets are not permitted in this context'
        ));
      }

      const maxBytes =
        context.maxInlineBytes !== undefined
          ? context.maxInlineBytes
          : DEFAULT_MAX_INLINE_BYTES;

      if (ref.data.length > maxBytes) {
        return blockedResult(makeErrorIssue(
          'policy.inline_asset_too_large',
          `Inline asset exceeds size limit: ${ref.data.length} bytes (max: ${maxBytes})`
        ));
      }

      return resolvedResult(ref.data, ref.mediaType);
    }

    case 'manifest': {
      if (context.manifest === undefined) {
        return missingResult(makeErrorIssue(
          'semantic.invalid_asset_reference',
          `Asset manifest not provided; cannot resolve asset '${ref.assetId}'`
        ));
      }

      const entry = context.manifest.assets.find(
        (e) => e.assetId === ref.assetId
      );

      if (entry === undefined) {
        return missingResult(makeErrorIssue(
          'semantic.invalid_asset_reference',
          `Asset '${ref.assetId}' not found in manifest`
        ));
      }

      return resolvedResult(null, entry.mediaType ?? null);
    }

    case 'relativePath': {
      // Remote URL check takes priority (DEC-010 conservative default)
      if (isRemoteUrl(ref.path)) {
        const allowlist = context.remoteUrlAllowlist ?? [];
        if (!checkRemoteUrlAllowlist(ref.path, allowlist)) {
          return blockedResult(makeErrorIssue(
            'policy.requires_feature_flag',
            'Remote URL assets are blocked by policy (DEC-010 conservative default)'
          ));
        }
        // URL is allowlisted — still apply allowRelativePaths check
      }

      if (context.allowRelativePaths === false) {
        return blockedResult(makeErrorIssue(
          'policy.capability_not_allowed',
          'Relative path assets are not permitted in this context'
        ));
      }

      if (ref.path.length === 0 || ref.path.includes('..')) {
        return blockedResult(makeErrorIssue(
          'semantic.invalid_asset_reference',
          'Invalid or potentially unsafe relative asset path'
        ));
      }

      return resolvedResult(ref.path, null);
    }

    default: {
      // Exhaustiveness guard — should not be reached with valid AssetRef types
      return {
        status: 'unsupported',
        data: null,
        mediaType: null,
        issues: [makeErrorIssue(
          'schema.unknown_kind',
          'Unsupported AssetRef kind encountered'
        )],
      };
    }
  }
}

/**
 * Resolves all AssetRefs from a list, returning one result per ref.
 * Preserves order. Never throws.
 */
export function resolveAssets(
  refs: AssetRef[],
  context: AssetResolutionContext
): AssetResolutionResult[] {
  return refs.map((ref) => resolveAsset(ref, context));
}
